import { useI18n } from '../i18n';
import {
  LOCKUP_VIEWBOX,
  SYMBOL_CARRY_PATH,
  SYMBOL_INK_PATH,
  WORDMARK_PATH,
} from '../ui/brandPaths';

export interface BrandLogoProps {
  /** Sizing — set the height; the width follows the lockup's own proportions. */
  className?: string | undefined;
}

/**
 * The Bitwize lockup: the symbol, then the `bitwize` wordmark.
 *
 * The geometry is the brand's master file, unmodified (`brandPaths.ts`). It is
 * the English wordmark in every locale: brand names stay in Latin script inside
 * Arabic text, and the Arabic transliteration is not yet confirmed. The symbol
 * does not mirror in RTL either, so one lockup serves both directions.
 *
 * The ink follows the theme and the carry does not, which is why this is an
 * inline SVG and not an `<img>`. Inside a `data-theme="dark"` band it re-inks
 * itself to White with no second file to keep in step with the first.
 *
 * The SVG carries the accessible name, so a link around it is named "Bitwize".
 */
export function BrandLogo({ className }: BrandLogoProps) {
  const { t } = useI18n();

  return (
    <svg
      viewBox={LOCKUP_VIEWBOX}
      className={className}
      role="img"
      aria-label={t('common.brand.name')}
      focusable="false"
    >
      <path fill="var(--hs-color-mark-ink)" fillRule="evenodd" d={SYMBOL_INK_PATH} />
      <path fill="var(--hs-color-mark-carry)" d={SYMBOL_CARRY_PATH} />
      <path fill="var(--hs-color-mark-ink)" d={WORDMARK_PATH} />
    </svg>
  );
}
